import {
  AnalyticsViewType,
  DeviceType,
  ExperienceDto,
  ExperienceIntegrationDto,
  ExperienceType,
  IntegrationName,
  S3FileLocationDto,
  StayAiExperienceSettingsType,
  StayAiGlobalSettingsType,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import { getExperienceFeFromDto } from "../../api/formatters/from-dto/experiences/experiences";
import { initialExperienceFeDto } from "../../api/initials";
import type { ActionMode, ViewMode } from "../../types";
import { ExperienceFe } from "../../types/schemas/experiences";
import { VariationFe } from "../../types/schemas/variations";
import { IExperienceState } from "../types/experiences";

const sliceExperienceInitialState: IExperienceState = {
  isSuccess: false,
  experience: undefined,
  storedExperienceState: undefined,
  selectedCampaignVariation: undefined,
  campaignChange: false,
  campaignBeingEdited: undefined,
  storedCampaignBeingEdited: undefined,
  campaignIsDuplicate: false,
  soloCampaignInTestBeingEdited: false,
};
const experienceSlice = rtk.createSlice({
  name: "experience",
  initialState: sliceExperienceInitialState,
  reducers: {
    initEnsuredExperienceFromDto: (
      draft,
      action: PayloadAction<{ experience: ExperienceDto }>
    ) => {
      draft.isSuccess = true;
      draft.experience = getExperienceFeFromDto(action.payload.experience);
    },
    resetCampaignBeingEdited: (draft) => {
      draft.campaignBeingEdited = undefined;
      draft.storedCampaignBeingEdited = undefined;
      draft.selectedCampaignVariation = undefined;
      draft.soloCampaignInTestBeingEdited = false;
      return draft;
    },
    initPriceExperienceFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
        defaultCurrency: string;
      }>
    ) => {
      draft.isSuccess = true;
      if (action.payload.experience) {
        const initializedState = getExperienceFeFromDto(
          action.payload.experience
        );
        draft.experience = initializedState;
        draft.experience.actionMode = "edit";

        draft.storedExperienceState = initializedState;
        draft.storedExperienceState.actionMode = "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          testFlowType: "pricing",
          useWideApp: false,
          defaultCurrency: action.payload.defaultCurrency,
          hasTestPricing: true,
          hasTestShipping: false,
          hasTestCampaign: false,
          hasTestContent: false,
          hasTestContentAdvanced: false,
          hasTestContentUrl: false,
          hasTestContentOnsite: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          hasTestOnsiteInjections: false,
        });
      }
    },
    initShippingExperienceFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
      }>
    ) => {
      draft.isSuccess = true;
      if (action.payload.experience) {
        draft.experience = getExperienceFeFromDto(action.payload.experience);
        draft.experience.actionMode = "edit";

        draft.storedExperienceState = getExperienceFeFromDto(
          action.payload.experience
        );
        draft.storedExperienceState.actionMode = "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          hasTestPricing: false,
          hasTestShipping: true,
          hasTestCampaign: false,
          hasTestContent: false,
          hasTestContentAdvanced: false,
          hasTestContentUrl: false,
          hasTestContentOnsite: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          testFlowType: "shipping",
          useWideApp: false,
          hasTestOnsiteInjections: false,
        });
      }
    },
    initContentExperienceFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
        testFlowType: ExperienceType;
      }>
    ) => {
      draft.isSuccess = true;
      if (action.payload.experience) {
        draft.experience = getExperienceFeFromDto(action.payload.experience);
        draft.storedExperienceState = getExperienceFeFromDto(
          action.payload.experience
        );
        draft.experience.actionMode = "edit";
        draft.storedExperienceState.actionMode = "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          hasTestPricing: false,
          hasTestShipping: false,
          hasTestCampaign: false,
          hasTestContent: true,
          testFlowType: action.payload.testFlowType,
          useWideApp: false,
          hasTestContentAdvanced:
            action.payload.testFlowType === "content/advanced",
          hasTestContentUrl: action.payload.testFlowType === "content/url",
          hasTestContentOnsite:
            action.payload.testFlowType === "content/onsiteEdits",
          hasTestContentTemplate:
            action.payload.testFlowType === "content/template",
          hasTestContentTheme: action.payload.testFlowType === "content/theme",
          hasTestOnsiteInjections: false,
        });
      }
    },
    initCampaignExperienceFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
        isSoloCampaign?: boolean;
      }>
    ) => {
      draft.isSuccess = true;
      if (action.payload.experience) {
        const initializedState = getExperienceFeFromDto(
          action.payload.experience
        );
        draft.experience = initializedState;
        draft.storedExperienceState = initializedState;
        draft.experience.actionMode = "edit";
        draft.storedExperienceState.actionMode = "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          testFlowType: "personalization/offer",
          useWideApp: false,
          defaultCurrency: undefined,
          hasTestPricing: false,
          hasTestShipping: false,
          hasTestCampaign: true,
          hasTestContent: false,
          hasTestContentAdvanced: false,
          hasTestContentUrl: false,
          hasTestContentOnsite: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          category: action.payload.isSoloCampaign
            ? "personalization"
            : "experiment",
          hasTestOnsiteInjections: false,
        });

        draft.experience.actionMode = "create";
      }
    },
    initPersonalizationFromDto: (
      draft,
      action: PayloadAction<{
        experience?: ExperienceDto;
        name?: string;
        description?: string;
      }>
    ) => {
      draft.isSuccess = true;
      if (action.payload.experience) {
        const initializedState = getExperienceFeFromDto(
          action.payload.experience
        );
        draft.experience = initializedState;
        draft.storedExperienceState = initializedState;
        draft.experience.actionMode = "edit";
        draft.storedExperienceState.actionMode = "edit";
      } else {
        draft.experience = initialExperienceFeDto({
          testFlowType: "personalization", // TODO - CP not sure if this is correct
          useWideApp: false,
          defaultCurrency: undefined,
          hasTestPricing: false,
          hasTestShipping: false,
          hasTestCampaign: false,
          hasTestContent: false,
          hasTestContentAdvanced: false,
          hasTestContentUrl: false,
          hasTestContentOnsite: false,
          hasTestContentTemplate: false,
          hasTestContentTheme: false,
          category: "personalization",
          hasTestOnsiteInjections: false,
        });
        if (action.payload.name) {
          draft.experience.name = action.payload.name;
        }
        if (action.payload.description) {
          draft.experience.description = action.payload.description;
        }
        draft.experience.actionMode = "create";
      }
    },
    initSoloCampaignExperience: (draft) => {
      draft.campaignBeingEdited = initialExperienceFeDto({
        testFlowType: "personalization/offer",
        useWideApp: false,
        defaultCurrency: undefined,
        hasTestPricing: false,
        hasTestShipping: false,
        hasTestCampaign: true,
        hasTestContent: false,
        hasTestContentAdvanced: false,
        hasTestContentUrl: false,
        hasTestContentOnsite: false,
        hasTestContentTemplate: false,
        hasTestContentTheme: false,
        category: "personalization",
        hasTestOnsiteInjections: false,
      });
      draft.soloCampaignInTestBeingEdited = true;
      draft.campaignBeingEdited.actionMode = "create";
    },
    resetExperience: (draft) => {
      draft = sliceExperienceInitialState;
      return draft;
    },
    setCampaignIsDuplicate: (
      draft,
      action: PayloadAction<{
        isDuplicate: boolean;
      }>
    ) => {
      draft.campaignIsDuplicate = action.payload.isDuplicate;
      return draft;
    },
    setSoloCampaignBeingEdited: (
      draft,
      action: PayloadAction<{
        value: boolean;
      }>
    ) => {
      draft.soloCampaignInTestBeingEdited = action.payload.value;
      return draft;
    },
    setCampaignBeingEdited: (
      draft,
      action: PayloadAction<{
        campaign: ExperienceDto | undefined;
      }>
    ) => {
      if (action.payload.campaign) {
        draft.campaignBeingEdited = getExperienceFeFromDto(
          action.payload.campaign
        );
        draft.storedCampaignBeingEdited = getExperienceFeFromDto(
          action.payload.campaign
        );
      }
      return draft;
    },
    setCampaignSelectedVariation: (
      draft,
      action: PayloadAction<{
        variation: VariationFe | undefined;
      }>
    ) => {
      draft.selectedCampaignVariation = action.payload.variation;
      return draft;
    },
    setIsExperienceCampaignChanged: (draft, action: PayloadAction<boolean>) => {
      draft.campaignChange = action.payload;
      return draft;
    },
    setExperienceId: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess) return draft;
      if (draft.experience) {
        draft.experience.id = action.payload;
      }
      return draft;
    },
    setExperienceStep: (draft, action: PayloadAction<number>) => {
      if (!draft.isSuccess || !draft.experience) return draft;
      draft.experience.step = action.payload;
      const url = new URL(window.location.href);
      url.searchParams.set("step", action.payload.toString());
      window.history.pushState(null, "", url.toString());
    },
    setExperienceName: (
      draft,
      action: PayloadAction<{ value: string; isCampaignBeingEdited?: boolean }>
    ) => {
      if (!draft.isSuccess) return draft;
      if (action.payload.isCampaignBeingEdited && draft.campaignBeingEdited) {
        draft.campaignBeingEdited.name = action.payload.value;
      } else if (draft.experience) {
        draft.experience.name = action.payload.value;
      }
      return draft;
    },
    setExperienceDescription: (
      draft,
      action: PayloadAction<{ value: string; isCampaignBeingEdited?: boolean }>
    ) => {
      if (!draft.isSuccess) return draft;
      if (action.payload.isCampaignBeingEdited && draft.campaignBeingEdited) {
        draft.campaignBeingEdited.description = action.payload.value;
      } else if (draft.experience) {
        draft.experience.description = action.payload.value;
      }
      return draft;
    },
    setExperienceGoal: (draft, action: PayloadAction<string>) => {
      if (!draft.isSuccess) return draft;
      if (draft.experience) {
        draft.experience.experienceAnalysis.goal = action.payload;
      }
      return draft;
    },
    setExperienceViewMode: (draft, action: PayloadAction<ViewMode>) => {
      if (!draft.isSuccess || !draft.experience) return draft;
      draft.experience.viewMode = action.payload;
    },
    setExperienceActionMode: (draft, action: PayloadAction<ActionMode>) => {
      if (!draft.isSuccess || !draft.experience) return draft;
      draft.experience.actionMode = action.payload;
    },
    setExperienceDevicePreview: (
      draft,
      action: PayloadAction<{
        device?: DeviceType;
        path?: string;
      }>
    ) => {
      if (!draft.isSuccess) return draft;

      if (action.payload.device && draft.experience) {
        if (draft.experience?.devicePreview) {
          draft.experience.devicePreview.device = action.payload.device;
        } else {
          draft.experience.devicePreview = {
            device: action.payload.device,
            path: "/",
          };
        }
      }

      if (action.payload.path !== undefined && draft.experience) {
        if (draft.experience?.devicePreview) {
          draft.experience.devicePreview.path = action.payload.path
            ? action.payload.path.startsWith("/")
              ? action.payload.path
              : `/${action.payload.path}`
            : "/";
        } else {
          draft.experience.devicePreview = {
            device: "desktop",
            path: action.payload.path
              ? action.payload.path.startsWith("/")
                ? action.payload.path
                : `/${action.payload.path}`
              : "/",
          };
        }
      }

      return draft;
    },
    setExperienceCurrency: (
      draft,
      action: PayloadAction<{
        code: string;
        symbol: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;
      draft.experience.currency = action.payload;
    },
    setExperienceShippingProfile: (
      draft,
      action: PayloadAction<{
        deliveryProfileId: string;
        deliveryLocationGroupId: string;
        deliveryZoneId: string;
        methodDefinitionId: string | null;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;

      if (draft.experience.shippingTestMethodDefinitions!.length < 1) {
        draft.experience.shippingTestMethodDefinitions!.push({
          deliveryProfileId: action.payload.deliveryProfileId,
          deliveryZoneId: action.payload.deliveryZoneId,
          deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
          methodDefinitionId: action.payload.methodDefinitionId,
        });
      } else {
        const zoneIndex =
          draft.experience.shippingTestMethodDefinitions!.findIndex(
            (def) => def.deliveryZoneId === action.payload.deliveryZoneId
          );
        if (zoneIndex === -1) {
          draft.experience.shippingTestMethodDefinitions!.push({
            deliveryProfileId: action.payload.deliveryProfileId,
            deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
            deliveryZoneId: action.payload.deliveryZoneId,
            methodDefinitionId: action.payload.methodDefinitionId,
          });
        } else {
          draft.experience.shippingTestMethodDefinitions!.splice(zoneIndex, 1);
        }
      }
    },
    setExperienceShippingTestMethodDefinition: (
      draft,
      action: PayloadAction<{
        deliveryProfileId: string;
        deliveryLocationGroupId: string;
        deliveryZoneId: string;
        methodDefinitionId: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;
      const deliveryZoneIndex =
        draft.experience.shippingTestMethodDefinitions!.findIndex(
          (def) =>
            def.deliveryZoneId === action.payload.deliveryZoneId &&
            !def.methodDefinitionId
        );

      const methodDefIndex =
        draft.experience.shippingTestMethodDefinitions!.findIndex(
          (def) => def.methodDefinitionId === action.payload.methodDefinitionId
        );
      if (methodDefIndex === -1) {
        // method def needs to be added
        if (deliveryZoneIndex === -1) {
          draft.experience.shippingTestMethodDefinitions!.push({
            deliveryProfileId: action.payload.deliveryProfileId,
            deliveryLocationGroupId: action.payload.deliveryLocationGroupId,
            deliveryZoneId: action.payload.deliveryZoneId,
            methodDefinitionId: action.payload.methodDefinitionId,
          });
        } else {
          draft.experience.shippingTestMethodDefinitions![
            deliveryZoneIndex
          ].methodDefinitionId = action.payload.methodDefinitionId;
        }
      } else {
        // remove
        draft.experience.shippingTestMethodDefinitions![methodDefIndex] = {
          ...draft.experience.shippingTestMethodDefinitions![methodDefIndex],
          methodDefinitionId: null,
        };
      }
    },
    setExperienceAnalyticsViewType: (
      draft,
      action: PayloadAction<AnalyticsViewType>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;

      draft.experience.experienceAnalysis.analyticsViewType = action.payload;
    },
    toggleRequiresLink: (
      draft,
      action: PayloadAction<{ requiresLink: boolean }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;

      draft.experience.requiresLink = action.payload.requiresLink;
      return draft;
    },
    setLinkBaseUrl: (draft, action: PayloadAction<{ linkBaseUrl: string }>) => {
      if (!draft.isSuccess || !draft.experience) return draft;

      draft.experience.linkBaseUrl = action.payload.linkBaseUrl;
      return draft;
    },
    deleteShippingExpMethodDefinitionById: (
      draft,
      action: PayloadAction<string>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;

      const methodDefIndexRemove =
        draft.experience.shippingTestMethodDefinitions!.findIndex(
          (def) => def.methodDefinitionId === action.payload
        );
      draft.experience.shippingTestMethodDefinitions![
        methodDefIndexRemove
      ].methodDefinitionId = null;

      draft.experience.shippingTestMethodDefinitions!.filter(
        (thing, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.deliveryZoneId === thing.deliveryZoneId &&
              t.methodDefinitionId === thing.methodDefinitionId
          )
      );
    },
    setExperienceIntegration: (
      draft,
      action: PayloadAction<{
        name: IntegrationName;
        experienceSettings: StayAiExperienceSettingsType;
        enabled: boolean;
      }>
    ) => {
      if (!draft.isSuccess || !draft.experience) return draft;

      if (!draft.experience.experienceIntegrations) {
        draft.experience.experienceIntegrations = [];
      }

      if (action.payload.name === "StayAi") {
        const stayAi = (
          draft.experience.experienceIntegrations || []
        ).findIndex((ei) => ei.integration.name === "StayAi");

        if (stayAi > -1) {
          draft.experience.experienceIntegrations[stayAi].experienceSettings =
            action.payload.experienceSettings;
          draft.experience.experienceIntegrations[stayAi].enabled =
            action.payload.enabled;
        } else {
          draft.experience.experienceIntegrations.push({
            integration: {
              enabled: true,
              description: "Stay.ai",
              name: "StayAi",
              apis: [],
              globalSettings: {},
            },
            enabled: action.payload.enabled,
            experienceSettings: action.payload.experienceSettings,
          });
        }
      }
    },
  },
  selectors: {
    selectedExperienceState: (state) => state,
    selectedExperience: (state) => state.experience as ExperienceFe,
    selectedCampaignBeingEdited: (state) =>
      state.campaignBeingEdited as ExperienceFe,
    selectedExperienceStayAiIntegration: (state) => {
      return (
        (state.experience as ExperienceFe).experienceIntegrations || []
      ).find((ei) => ei.integration.name === "StayAi") as
        | ExperienceIntegrationDto<
            "StayAi",
            StayAiGlobalSettingsType,
            StayAiExperienceSettingsType
          >
        | undefined;
    },
  },
});

export const {
  resetExperience,
  // Inits
  initContentExperienceFromDto,
  initCampaignExperienceFromDto,
  initShippingExperienceFromDto,
  initEnsuredExperienceFromDto,
  initPriceExperienceFromDto,
  // Sets
  setExperienceId,
  setExperienceStep,
  setExperienceName,
  setExperienceDescription,
  setExperienceGoal,
  setExperienceViewMode,
  setExperienceActionMode,
  setExperienceDevicePreview,
  setExperienceCurrency,
  setExperienceShippingProfile,
  setExperienceShippingTestMethodDefinition,
  setExperienceAnalyticsViewType,
  setExperienceIntegration,
  // Deletes
  deleteShippingExpMethodDefinitionById,
  // campaigns
  setCampaignIsDuplicate,
  setCampaignBeingEdited,
  setCampaignSelectedVariation,
  setIsExperienceCampaignChanged,
  toggleRequiresLink,
  setLinkBaseUrl,
  initSoloCampaignExperience,
  resetCampaignBeingEdited,
  setSoloCampaignBeingEdited,
  // personalizations
  initPersonalizationFromDto,
} = experienceSlice.actions;

export const {
  selectedExperienceState,
  selectedExperience,
  selectedCampaignBeingEdited,
  selectedExperienceStayAiIntegration,
} = experienceSlice.selectors;

export const experienceReducer = experienceSlice.reducer;
